/**
 * AlgoGreeks HFT - Professional Landing Page
 * SESSION 131: Simplified design - Hero + Stats + Contact Bar
 */
import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useSearchParams, useNavigate } from 'react-router-dom';
import Marquee from 'react-fast-marquee';
import { 
  TrendingUp, 
  Target, 
  Zap, 
  Shield, 
  ChevronRight,
  Play,
  Send,
  HelpCircle,
  ChevronDown,
  ChevronUp,
  IndianRupee,
  Check,
  Sparkles
} from 'lucide-react';

// Error messages for auth
const ERROR_MESSAGES = {
  google_auth_failed: 'Google authentication failed. Please try again.',
  no_auth_code: 'Authentication was cancelled.',
  token_exchange_failed: 'Failed to verify with Google. Please try again.',
  no_email: 'Could not retrieve email from Google.',
  account_inactive: 'Your account is pending approval. Please contact admin.',
  subscription_expired: 'Your subscription has expired. Please contact admin.',
  server_error: 'Server error. Please try again later.',
  no_token: 'No authentication token received. Please sign in again.',
  invalid_token: 'Your session has expired. Please sign in again.',
  invite_only: 'Registration is invite-only. Please contact admin for an invite link.',
  invite_email_mismatch: 'This invite was generated for a different email address. Please sign in with the correct Google account.',
};

// Mock live ticker data
const TICKER_DATA = [
  { symbol: 'NIFTY 50', price: '22,713.10', change: '+0.2%', positive: true },
  { symbol: 'BANKNIFTY', price: '51,548.75', change: '+0.3%', positive: true },
  { symbol: 'FINNIFTY', price: '24,890.50', change: '-0.1%', positive: false },
  { symbol: 'SENSEX', price: '73,319.55', change: '+0.2%', positive: true },
];

// Stats data
const STATS = [
  { value: '85%+', label: 'Signal Accuracy', icon: Target },
  { value: '< 1ms', label: 'Latency', icon: Zap },
  { value: '24/7', label: 'Monitoring', icon: Shield },
  { value: '500+', label: 'Active Traders', icon: TrendingUp },
];

// 147j+16by — Subscription pricing tiers (INR). 6-month is highlighted as
// "Most Popular" — best balance of commitment vs price. All prices are
// per-user, invite-only access. Onboarding handled manually by admin
// after payment confirmation via Telegram.
const PRICING_PLANS = [
  {
    id: 'q3m',
    label: '3 Months',
    price: '₹3,000',
    monthly: '₹1,000/mo',
    tag: null,
    perks: [
      'Full AlphaScout access (Largecap, Mid & Small, Value)',
      'Live Order Wins feed with Deep Analysis',
      'Smart-money bulk-deal tracking',
      'Daily Telegram alerts',
    ],
  },
  {
    id: 'q6m',
    label: '6 Months',
    price: '₹5,000',
    monthly: '₹833/mo',
    tag: 'MOST POPULAR',
    perks: [
      'Everything in 3 Months',
      '17% savings vs quarterly',
      'Priority support on Telegram',
      'Early access to new research engines',
    ],
  },
  {
    id: 'q1y',
    label: '1 Year',
    price: '₹8,000',
    monthly: '₹667/mo',
    tag: 'BEST VALUE',
    perks: [
      'Everything in 6 Months',
      '33% savings vs quarterly',
      'Direct line to founder for feedback',
      'Lock in current pricing for 12 months',
    ],
  },
];

// FAQ Data - SESSION 122
const FAQ_DATA = [
  {
    question: "Is there a way to tell the user that score above 100 is required to enter into trade?",
    answer: "A score of 100 doesn't always mean a buy, because there are some strict vetoes also. If the trade passes all the vetoes and the score is above a given level, then the trade is fired in the Signal section."
  },
  {
    question: "If the score is above 100, is there a way to know if I can still enter the trade? (e.g., signal came at 10am and I look at 11am with score still 100+)",
    answer: "There is always a T1 and SL mentioned in the trade. Otherwise one can follow strict target of 50 points and SL of 30 points. If the user looks at it late, then they should ideally wait for another signal."
  },
  {
    question: "Is there any description for Equity signals that these are intraday only and users can trade in futures?",
    answer: "Most of the trades are advised intraday but one can carry it for maximum 1-2 days. Trade prices are in futures only."
  },
  {
    question: "How is the trade shown once it is complete?",
    answer: "It will show closed as either T1 or SL hit."
  },
  {
    question: "Are there any other info that the user should look at apart from the signal-log?",
    answer: "For scalpers, if they see a score above 90 but do not see it in signal section, they can trade with strict profit of 20 points and SL of 20 points too."
  }
];

export const LandingPage = () => {
  const { loginWithGoogle, loading, isAuthenticated } = useAuth();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const error = searchParams.get('error');
  const [scrolled, setScrolled] = useState(false);
  const [showVideoModal, setShowVideoModal] = useState(false);
  const [showPricingModal, setShowPricingModal] = useState(false);  // 147j+16ca: hero-level pricing modal
  const [expandedFAQ, setExpandedFAQ] = useState(null);  // SESSION 122: FAQ state

  // Demo video URL (non-downloadable)
  const DEMO_VIDEO_URL = "https://customer-assets.emergentagent.com/job_fc1f1c09-8af0-4603-ba07-bce2c6ca7a94/artifacts/eh38adsv_HFT%20Apr%202.MOV";

  // Handle scroll for header effect
  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated) {
      navigate('/');
    }
  }, [isAuthenticated, navigate]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#050505]">
        <div className="w-10 h-10 border-2 border-cyan-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505] text-white overflow-x-hidden" data-testid="landing-page">
      {/* Noise Texture Overlay */}
      <div className="fixed inset-0 opacity-[0.03] pointer-events-none z-50"
        style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)'/%3E%3C/svg%3E")`,
        }}
      />

      {/* Fixed Header */}
      <header 
        className={`fixed top-0 left-0 right-0 z-40 transition-all duration-300 ${
          scrolled 
            ? 'bg-black/80 backdrop-blur-xl border-b border-white/10' 
            : 'bg-transparent'
        }`}
        data-testid="landing-header"
      >
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          {/* Logo */}
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center shadow-lg shadow-cyan-500/20">
              <span className="text-white font-black text-lg tracking-tighter">AG</span>
            </div>
            <div>
              <span className="text-xl font-bold tracking-tight">AlgoGreeks</span>
              <span className="hidden sm:inline text-xs text-gray-500 ml-2">HFT Engine</span>
            </div>
          </div>

          {/* Empty - contacts moved to fixed bottom bar */}
          <div></div>
        </div>
      </header>

      {/* Live Ticker Strip */}
      <div className="fixed top-[72px] left-0 right-0 z-30 bg-black/60 backdrop-blur-md border-b border-white/5">
        <Marquee gradient={false} speed={40} className="py-2">
          {[...TICKER_DATA, ...TICKER_DATA].map((item, i) => (
            <div key={i} className="flex items-center gap-6 mx-8">
              <span className="text-gray-400 font-medium text-sm">{item.symbol}</span>
              <span className="font-mono font-bold text-white">{item.price}</span>
              <span className={`font-mono text-sm ${item.positive ? 'text-green-400' : 'text-red-400'}`}>
                {item.change}
              </span>
            </div>
          ))}
        </Marquee>
      </div>

      {/* Hero Section */}
      {/* 147j+16cb — Hero padding tightened (pt-40 pb-20 → pt-28 pb-10)
          to leave enough room above the fold for the stats row to be
          visible even when a browser bookmarks bar is open (~30px tax). */}
      <section className="relative pt-28 pb-10 px-6 overflow-hidden">
        {/* Background Effects */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          {/* Gradient Orbs */}
          <div className="absolute top-1/4 left-1/4 w-[600px] h-[600px] bg-cyan-500/10 rounded-full blur-[120px]" />
          <div className="absolute bottom-1/4 right-1/4 w-[500px] h-[500px] bg-purple-500/10 rounded-full blur-[100px]" />
          
          {/* Grid Pattern */}
          <div 
            className="absolute inset-0 opacity-[0.03]"
            style={{
              backgroundImage: `linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px),
                               linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)`,
              backgroundSize: '60px 60px',
            }}
          />
        </div>

        <div className="relative max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Hero Content */}
            <div>
              {/* Badge */}
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/5 border border-white/10 mb-5">
                <div className="w-2 h-2 rounded-full bg-green-400 animate-pulse" />
                <span className="text-sm text-gray-300">Live Smart Money Tracking</span>
              </div>

              {/* Main Headline */}
              <h1 
                className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight leading-[1.1] mb-4"
                style={{ fontFamily: "'Barlow Condensed', sans-serif" }}
              >
                <span className="text-white">Trade with</span>
                <br />
                <span className="bg-gradient-to-r from-cyan-400 via-blue-500 to-purple-500 bg-clip-text text-transparent">
                  Institutional Edge
                </span>
              </h1>

              {/* Subheadline */}
              <p className="text-base sm:text-lg text-gray-400 max-w-xl mb-6 leading-relaxed">
                Track smart money movements and institutional activity in real-time with our proprietary
                <span className="text-cyan-400 font-semibold"> God Score</span> algorithm. 
                See what the big players see.
              </p>

              {/* Error Message — Shown prominently BEFORE CTAs */}
              {error && (
                <div className="max-w-xl p-4 rounded-xl bg-red-500/10 border border-red-500/20 mb-8 animate-in fade-in slide-in-from-top-2 duration-300" data-testid="login-error">
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 rounded-full bg-red-500/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <svg className="w-4 h-4 text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                      </svg>
                    </div>
                    <div>
                      <p className="text-red-400 font-semibold text-sm mb-1">Access Denied</p>
                      <p className="text-red-400/80 text-sm">{ERROR_MESSAGES[error] || `Authentication error: ${error}`}</p>
                    </div>
                  </div>
                </div>
              )}

              {/* CTA Buttons */}
              <div className="flex flex-wrap items-center gap-3">
                <a
                  href="https://t.me/hftalgogreeksPerformance"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="hero-telegram-btn"
                  className="group px-6 py-3 rounded-xl bg-gradient-to-r from-cyan-500 to-blue-600 
                           text-white font-bold text-base shadow-xl shadow-cyan-500/25
                           hover:shadow-cyan-500/40 transition-all duration-300 hover:scale-105
                           flex items-center gap-2 no-underline"
                >
                  <Send className="w-4 h-4" />
                  <span>Join our Telegram</span>
                  <ChevronRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </a>

                <button
                  onClick={() => setShowVideoModal(true)}
                  data-testid="watch-demo-btn"
                  className="px-6 py-3 rounded-xl border border-white/20 text-white font-semibold text-base
                           hover:bg-white/5 transition-all duration-200 flex items-center gap-2"
                >
                  <Play className="w-4 h-4" />
                  <span>Watch Demo</span>
                </button>

                {/* 147j+16ca — Pricing CTA. Opens a modal with the three
                    subscription tiers so the offer is one click away
                    from the hero without forcing a scroll. */}
                <button
                  onClick={() => setShowPricingModal(true)}
                  data-testid="hero-pricing-btn"
                  className="px-6 py-3 rounded-xl border border-cyan-500/40 text-cyan-300 font-semibold text-base
                           bg-cyan-500/[0.06] hover:bg-cyan-500/[0.12] transition-all duration-200 flex items-center gap-2"
                >
                  <IndianRupee className="w-4 h-4" />
                  <span>Pricing</span>
                </button>
              </div>

              {/* Invite-only message */}
              <div className="mt-4 flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-cyan-500 animate-pulse"></div>
                <p className="text-sm text-gray-500">
                  Platform access is invite-only. <button onClick={loginWithGoogle} className="text-cyan-400 hover:text-cyan-300 underline underline-offset-2">Login</button> if you already have an account.
                </p>
              </div>
            </div>

            {/* Dashboard Preview Image */}
            <div className="relative">
              <div className="relative rounded-2xl overflow-hidden border border-white/10 shadow-2xl shadow-black/50">
                {/* Browser Frame */}
                <div className="bg-gray-900/80 px-4 py-3 flex items-center gap-2 border-b border-white/10">
                  <div className="flex gap-1.5">
                    <div className="w-3 h-3 rounded-full bg-red-500/80" />
                    <div className="w-3 h-3 rounded-full bg-yellow-500/80" />
                    <div className="w-3 h-3 rounded-full bg-green-500/80" />
                  </div>
                  <div className="flex-1 mx-4 px-3 py-1 rounded-md bg-black/40 text-xs text-gray-500 font-mono">
                    hft.algogreeks.com
                  </div>
                </div>
                
                {/* Dashboard Screenshot Placeholder */}
                <div className="aspect-[16/10] bg-gradient-to-br from-gray-900 to-black relative">
                  <img 
                    src="https://images.unsplash.com/photo-1651341050677-24dba59ce0fd?w=1200&q=80"
                    alt="AlgoGreeks Trading Dashboard"
                    className="w-full h-full object-cover opacity-80"
                  />
                  {/* Overlay with mock UI elements */}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent" />
                  
                  {/* Mock God Score Badge */}
                  <div className="absolute top-4 right-4 px-4 py-2 rounded-lg bg-black/60 backdrop-blur-sm border border-green-500/30">
                    <div className="text-xs text-gray-400 mb-1">God Score</div>
                    <div className="text-2xl font-black text-green-400 font-mono">+97</div>
                  </div>
                  
                  {/* Mock Signal */}
                  <div className="absolute bottom-4 left-4 right-4 p-4 rounded-lg bg-black/60 backdrop-blur-sm border border-cyan-500/30">
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="text-xs text-gray-400">NIFTY Signal</div>
                        <div className="text-lg font-bold text-green-400 flex items-center gap-2">
                          <TrendingUp className="w-4 h-4" />
                          LONG @ 22,650
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-xs text-gray-400">Target</div>
                        <div className="text-lg font-bold text-cyan-400">22,770</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Floating Glow Effect */}
              <div className="absolute -inset-4 bg-gradient-to-r from-cyan-500/20 to-purple-500/20 blur-3xl -z-10" />
            </div>
          </div>
        </div>
      </section>

      {/* Stats Strip */}
      <section className="relative py-8 border-y border-white/5 bg-black/40">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {STATS.map((stat, i) => (
              <div key={i} className="text-center" data-testid={`stat-${i}`}>
                <div className="flex items-center justify-center gap-2 mb-2">
                  <stat.icon className="w-5 h-5 text-cyan-400" />
                </div>
                <div className="text-3xl sm:text-4xl font-black text-white mb-1" style={{ fontFamily: "'JetBrains Mono', monospace" }}>
                  {stat.value}
                </div>
                <div className="text-sm text-gray-500">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 147j+16ca — Pricing section removed from page flow.
          Pricing is now accessible via the hero "Pricing" button
          which opens <PricingModal /> at the bottom of this file. */}

      {/* FAQ Section - SESSION 122 */}
      <section className="relative py-16 bg-gradient-to-b from-black/40 to-[#050505]" data-testid="faq-section">
        <div className="max-w-3xl mx-auto px-6">
          {/* Section Header */}
          <div className="text-center mb-10">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-cyan-500/10 border border-cyan-500/20 mb-4">
              <HelpCircle className="w-4 h-4 text-cyan-400" />
              <span className="text-sm text-cyan-400 font-medium">FAQ</span>
            </div>
            <h2 className="text-2xl sm:text-3xl font-bold text-white mb-2">
              Frequently Asked Questions
            </h2>
            <p className="text-gray-400 text-sm">
              Everything you need to know about trading signals
            </p>
          </div>

          {/* FAQ Accordion */}
          <div className="space-y-3">
            {FAQ_DATA.map((faq, index) => (
              <div
                key={index}
                className={`border rounded-xl overflow-hidden transition-all duration-300 ${
                  expandedFAQ === index 
                    ? 'border-cyan-500/50 bg-cyan-500/5' 
                    : 'border-white/10 hover:border-white/20 bg-white/[0.02]'
                }`}
                data-testid={`faq-item-${index}`}
              >
                {/* Question */}
                <button
                  onClick={() => setExpandedFAQ(expandedFAQ === index ? null : index)}
                  className="w-full flex items-start justify-between gap-4 p-5 text-left"
                >
                  <div className="flex items-start gap-3">
                    <span className="flex-shrink-0 w-7 h-7 rounded-full bg-cyan-500/20 text-cyan-400 text-sm font-bold flex items-center justify-center mt-0.5">
                      {index + 1}
                    </span>
                    <span className="text-sm sm:text-base font-medium text-gray-200 leading-relaxed">
                      {faq.question}
                    </span>
                  </div>
                  <div className="flex-shrink-0 mt-1">
                    {expandedFAQ === index ? (
                      <ChevronUp className="w-5 h-5 text-cyan-400" />
                    ) : (
                      <ChevronDown className="w-5 h-5 text-gray-500" />
                    )}
                  </div>
                </button>

                {/* Answer - Collapsible */}
                <div
                  className={`overflow-hidden transition-all duration-300 ease-in-out ${
                    expandedFAQ === index ? 'max-h-96' : 'max-h-0'
                  }`}
                >
                  <div className="px-5 pb-5 pt-0">
                    <div className="ml-10 pl-4 border-l-2 border-cyan-500/30">
                      <p className="text-sm sm:text-base text-cyan-300 font-semibold italic leading-relaxed">
                        {faq.answer}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Pro Tip */}
          <div className="mt-8 p-4 rounded-xl bg-amber-500/10 border border-amber-500/20">
            <p className="text-sm text-amber-400/90 text-center">
              <span className="font-bold">Pro Tip:</span> Always check the Signal section for confirmed trades with defined T1/SL levels before entering any position.
            </p>
          </div>
        </div>
      </section>

      {/* Footer - with bottom padding for fixed contact bar */}
      <footer className="border-t border-white/5 py-6 pb-20 px-6">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center">
              <span className="text-white font-bold text-xs">AG</span>
            </div>
            <span className="text-gray-400 text-sm">AlgoGreeks HFT v2.0</span>
          </div>
          
          <div className="flex items-center gap-4 text-xs text-gray-500">
            <a href="#" className="hover:text-white transition-colors">Privacy</a>
            <a href="#" className="hover:text-white transition-colors">Terms</a>
            <span className="text-gray-600">© 2026 AlgoGreeks</span>
          </div>
        </div>
      </footer>

      {/* Fixed Bottom Contact Bar - Mobile Optimized */}
      <div className="fixed bottom-0 left-0 right-0 z-50 bg-black/95 backdrop-blur-lg border-t border-white/10" data-testid="contact-bar">
        <div className="max-w-7xl mx-auto px-2 sm:px-4 py-2 sm:py-3 flex items-center justify-center gap-3 sm:gap-8">
          {/* WhatsApp */}
          <a 
            href="https://wa.me/919311539273"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-1.5 sm:gap-2 text-white hover:text-green-400 transition-colors group"
            data-testid="bottom-whatsapp"
          >
            <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-green-500 flex items-center justify-center group-hover:scale-110 transition-transform">
              <svg className="w-4 h-4 sm:w-5 sm:h-5 text-white" fill="currentColor" viewBox="0 0 24 24">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
              </svg>
            </div>
          </a>

          {/* Phone Number - Clickable */}
          <a 
            href="tel:+919311539273"
            className="flex items-center gap-1.5 sm:gap-2 group"
            data-testid="bottom-phone"
          >
            <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-cyan-500/20 border border-cyan-500/30 flex items-center justify-center group-hover:bg-cyan-500/30 transition-colors">
              <svg className="w-4 h-4 sm:w-5 sm:h-5 text-cyan-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
              </svg>
            </div>
            <span className="text-white font-bold text-sm sm:text-base tracking-wide group-hover:text-cyan-400 transition-colors" style={{ fontFamily: "'JetBrains Mono', monospace" }}>
              +91 93115 39273
            </span>
          </a>

          {/* YouTube */}
          <a 
            href="https://www.youtube.com/@HFT.ALGOGREEKS"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-1.5 sm:gap-2 text-white hover:text-red-400 transition-colors group"
            data-testid="bottom-youtube"
          >
            <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-red-500 flex items-center justify-center group-hover:scale-110 transition-transform">
              <svg className="w-4 h-4 sm:w-5 sm:h-5 text-white" fill="currentColor" viewBox="0 0 24 24">
                <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
              </svg>
            </div>
          </a>
        </div>
      </div>

      {/* Video Modal */}
      {showVideoModal && (
        <div 
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/90 backdrop-blur-sm"
          onClick={() => setShowVideoModal(false)}
          data-testid="video-modal"
        >
          <div 
            className="relative w-full max-w-4xl mx-4"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close Button */}
            <button
              onClick={() => setShowVideoModal(false)}
              className="absolute -top-12 right-0 text-white/60 hover:text-white transition-colors flex items-center gap-2"
              data-testid="close-video-modal"
            >
              <span className="text-sm">Close</span>
              <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
            
            {/* Video Container */}
            <div className="relative rounded-2xl overflow-hidden border border-white/10 bg-black shadow-2xl">
              <video
                autoPlay
                controls
                controlsList="nodownload"
                disablePictureInPicture
                onContextMenu={(e) => e.preventDefault()}
                className="w-full aspect-video"
                playsInline
              >
                <source src={DEMO_VIDEO_URL} type="video/quicktime" />
                <source src={DEMO_VIDEO_URL} type="video/mp4" />
                Your browser does not support the video tag.
              </video>
            </div>
            
            {/* Video Title */}
            <div className="mt-4 text-center">
              <h3 className="text-xl font-bold text-white">AlgoGreeks HFT Demo</h3>
              <p className="text-gray-400 text-sm mt-1">See the God Score Engine in action</p>
            </div>
          </div>
        </div>
      )}

      {/* 147j+16ca — Pricing Modal. Opens from the hero "Pricing"
          button. Renders the three subscription tiers as cards;
          each card's "Get Access" CTA opens WhatsApp with a
          pre-filled subscribe message. Click-outside to close. */}
      {showPricingModal && (
        <div
          className="fixed inset-0 z-[100] flex items-start justify-center bg-black/90 backdrop-blur-sm overflow-y-auto py-8 px-4"
          onClick={() => setShowPricingModal(false)}
          data-testid="pricing-modal"
        >
          <div
            className="relative w-full max-w-6xl my-auto"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close Button */}
            <button
              onClick={() => setShowPricingModal(false)}
              className="absolute -top-2 right-0 text-white/60 hover:text-white transition-colors flex items-center gap-2"
              data-testid="close-pricing-modal"
            >
              <span className="text-sm">Close</span>
              <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>

            {/* Modal Content */}
            <div className="mt-8 rounded-2xl border border-white/10 bg-gradient-to-b from-[#0a0a0a] to-[#050505] p-6 sm:p-10 shadow-2xl">
              {/* Header */}
              <div className="text-center mb-8">
                <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-cyan-500/10 border border-cyan-500/20 mb-4">
                  <IndianRupee className="w-4 h-4 text-cyan-400" />
                  <span className="text-sm text-cyan-400 font-medium">PRICING</span>
                </div>
                <h2 className="text-2xl sm:text-3xl font-bold text-white mb-2">
                  Simple, transparent pricing
                </h2>
                <p className="text-gray-400 text-sm">
                  Invite-only access · Pay once, trade smarter for months
                </p>
              </div>

              {/* Cards */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {PRICING_PLANS.map((plan) => {
                  const isPopular = plan.tag === 'MOST POPULAR';
                  return (
                    <div
                      key={plan.id}
                      data-testid={`pricing-card-${plan.id}`}
                      className={`relative rounded-2xl border p-6 flex flex-col transition-all duration-300 ${
                        isPopular
                          ? 'border-cyan-500/60 bg-gradient-to-b from-cyan-500/[0.08] to-transparent shadow-[0_0_40px_-12px_rgba(34,211,238,0.35)] md:scale-[1.03]'
                          : 'border-white/10 bg-white/[0.02] hover:border-white/20'
                      }`}
                    >
                      {plan.tag && (
                        <div
                          className={`absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full text-[10px] font-bold tracking-wider flex items-center gap-1 ${
                            isPopular
                              ? 'bg-cyan-500 text-black'
                              : 'bg-purple-500/20 text-purple-300 border border-purple-500/40'
                          }`}
                          data-testid={`pricing-tag-${plan.id}`}
                        >
                          {isPopular && <Sparkles className="w-3 h-3" />}
                          {plan.tag}
                        </div>
                      )}

                      <div className="text-sm font-medium text-gray-400 uppercase tracking-wider">
                        {plan.label}
                      </div>

                      <div className="mt-4 flex items-baseline gap-2">
                        <span
                          className="text-4xl font-black text-white"
                          style={{ fontFamily: "'JetBrains Mono', monospace" }}
                          data-testid={`pricing-amount-${plan.id}`}
                        >
                          {plan.price}
                        </span>
                      </div>
                      <div className="mt-1 text-xs text-gray-500">
                        Effective {plan.monthly}
                      </div>

                      <div className="my-5 border-t border-white/5" />

                      <ul className="space-y-2.5 flex-1">
                        {plan.perks.map((perk, idx) => (
                          <li key={idx} className="flex items-start gap-2 text-sm text-gray-300">
                            <Check
                              className={`w-4 h-4 mt-0.5 shrink-0 ${
                                isPopular ? 'text-cyan-400' : 'text-emerald-400'
                              }`}
                            />
                            <span>{perk}</span>
                          </li>
                        ))}
                      </ul>

                      <a
                        href="https://wa.me/919311539273?text=Hi%2C%20I'd%20like%20to%20subscribe%20to%20AlgoGreeks%20-%20{plan}"
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid={`pricing-cta-${plan.id}`}
                        className={`mt-6 inline-flex items-center justify-center gap-1.5 w-full px-4 py-2.5 rounded-lg text-sm font-semibold transition-all ${
                          isPopular
                            ? 'bg-cyan-500 hover:bg-cyan-400 text-black'
                            : 'bg-white/[0.04] hover:bg-white/[0.08] text-white border border-white/10'
                        }`}
                      >
                        Get Access
                        <ChevronRight className="w-4 h-4" />
                      </a>
                    </div>
                  );
                })}
              </div>

              {/* Footnote */}
              <p className="mt-8 text-center text-xs text-gray-500">
                All plans include both AlphaScout (Largecap, Mid &amp; Small, Value) and Order Wins research.
                Access is granted within 24 hours of payment confirmation. Questions?{' '}
                <a
                  href="https://wa.me/919311539273"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-cyan-400 hover:underline"
                  data-testid="pricing-contact-link"
                >
                  Ping us on WhatsApp
                </a>
                .
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LandingPage;
